import { Base } from '../../abstract/base.model';
import { FieldTestChecklistItem } from './field-test-checklist-item.model';
import { FieldTestStatus } from './field-test-status.model';
import { FieldTestType } from './field-test-type.model';
import { isRecord } from '../utils/is-record';
import { toChecklist } from '../utils/field-test-utils';
import { nullableString } from '../utils/wire-value-utils';

/**
 * A pre-flight / post-flight / equipment / calibration field test, built from the hub's field test JSON
 * (`user-field-tests-controller.ts`, hub `data-models/field-test.ts`).
 * The web renames `aircraftName` to `aircraft`, `pilotName` to `pilot` and `testDate` to `date`.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). Display fallbacks belong in templates.
 */
export class FieldTest extends Base {
  // Writable fields
  title?: string | null;
  testType?: FieldTestType | null;
  aircraftId?: string | null;
  aircraft?: string | null;
  pilotId?: string | null;
  pilot?: string | null;
  date?: string | null;
  location?: string | null;
  checklist?: FieldTestChecklistItem[] | null;
  notes?: string | null;

  // Read-only fields
  organizationId?: string | null;
  /** Computed by the hub from the checklist; a missing status reads as 'pending'. */
  status: FieldTestStatus;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.title = nullableString(source['title']);
    this.testType = nullableString(source['testType']) as FieldTestType | null | undefined;
    this.aircraftId = nullableString(source['aircraftId']);
    this.aircraft = nullableString(source['aircraftName']);
    this.pilotId = nullableString(source['pilotId']);
    this.pilot = nullableString(source['pilotName']);
    this.date = nullableString(source['testDate']);
    this.location = nullableString(source['location']);
    this.checklist = toChecklist(source['checklist']);
    this.notes = nullableString(source['notes']);
    this.organizationId = nullableString(source['organizationId']);
    this.status = (nullableString(source['status']) ?? 'pending') as FieldTestStatus;
  }
}
